import { Component } from 'react';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';

import { PlainHorizontalCardPadding } from '../../../../../../../src/packages/kakapo-ui-kit/components/cards/PlainHorizontalCard';
import { ImageFile } from '../../../../../../../src/packages/kakapo-ui-kit/types/Image';
import { ImageUploadDropZone } from './ImageUploadDropZone';

interface IProps extends WithTranslation {
	// Accepted file-types, eg. image/png, image/jpeg
	accept: Array<string>;

	// Name of the entity of this input, to be used within translations
	translationPropertyKey: string;

	paddingSize?: PlainHorizontalCardPadding;

	onError: (error: Error) => void;
	onFileSelected: (fileUrl: ImageFile, fileData: File) => void;
	hasVideo?: boolean;
}

interface IState {
	isDragging: boolean;
}

class ClassImageUpload extends Component<IProps, IState> {
	inputRef: React.RefObject<HTMLInputElement>;

	dragCounter: number = 0;

	constructor(props: IProps) {
		super(props);

		this.inputRef = React.createRef();

		window.addEventListener('dragover', this.cancelDefaultBehaviour);
		window.addEventListener('drop', this.cancelDefaultBehaviour);

		this.state = {
			isDragging: false,
		};
	}

	componentDidMount() {
		const ref = this.inputRef.current!;

		ref.addEventListener('dragstart', this.handleDragStart);
		ref.addEventListener('dragenter', this.handleDragIn);
		ref.addEventListener('dragleave', this.handleDragOut);
		ref.addEventListener('dragover', this.cancelDefaultBehaviour);
		ref.addEventListener('drop', this.handleDrop);
	}

	componentWillUnmount() {
		const ref = this.inputRef.current!;

		ref.removeEventListener('dragstart', this.handleDragStart);
		ref.removeEventListener('dragenter', this.handleDragIn);
		ref.removeEventListener('dragleave', this.handleDragOut);
		ref.removeEventListener('dragover', this.cancelDefaultBehaviour);
		ref.removeEventListener('drop', this.handleDrop);
	}

	onSelectFile = (event: React.ChangeEvent<HTMLInputElement>) => {
		const {
			target: { files },
		} = event;
		const { onError } = this.props;

		this.loadFile(files!).catch((error: Error) => onError(error));
	};

	cancelDefaultBehaviour = (event: DragEvent) => {
		event.preventDefault();
		event.stopPropagation();
	};

	handleDragStart = ({ dataTransfer }: DragEvent) => dataTransfer?.clearData();

	handleDragIn = ({ dataTransfer }: DragEvent) => {
		if (dataTransfer!.items == null || dataTransfer!.items.length <= 0) {
			return;
		}

		this.dragCounter += 1;
		this.setState({ isDragging: true });
	};

	handleDragOut = () => {
		this.dragCounter -= 1;

		if (this.dragCounter === 0) {
			this.setState({ isDragging: false });
		}
	};

	handleDrop = async ({ dataTransfer }: DragEvent) => {
		if (dataTransfer == null) {
			return;
		}

		const { onError } = this.props;
		this.setState({ isDragging: false });

		this.loadFile(dataTransfer.files)
			.catch((error) => onError(error))
			.finally(() => {
				this.dragCounter = 0;
			});
	};

	loadFile = async (files: FileList) => {
		const { onFileSelected } = this.props;

		if (files == null || files.length <= 0) {
			throw new Error('No file to load.');
		}

		const reader = new FileReader();
		reader.addEventListener('load', () => {
			onFileSelected(reader.result!, files[0]);
		});

		reader.readAsDataURL(files[0]);
	};

	render() {
		const { accept, translationPropertyKey, paddingSize } = this.props;
		const { isDragging } = this.state;

		return (
			<ImageUploadDropZone
				inputRef={this.inputRef}
				isDragging={isDragging}
				accept={accept}
				translationPropertyKey={translationPropertyKey}
				onSelectFile={this.onSelectFile}
				paddingSize={paddingSize}
				hasVideo={this.props.hasVideo}
			/>
		);
	}
}

export const ImageUpload = withTranslation()(ClassImageUpload);
